/**
 * Marketing Footer Component
 * Footer for marketing pages using Carbon Design System
 */

import React from 'react';
import { Grid, Column, Tag, Link } from '@carbon/react';
import {
  LogoTwitter,
  LogoLinkedin,
  LogoFacebook,
  LogoYoutube,
  Email,
  Phone,
  Location,
  Security,
  CheckmarkFilled,
} from '@carbon/icons-react';
import './MarketingFooter.scss';

const MarketingFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Demo', href: '/demo' },
        { label: 'Free Trial', href: '/trial' },
        { label: 'API Documentation', href: '/docs/api' },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { label: 'For Educators', href: '/educators' },
        { label: 'For Parents', href: '/parents' },
        { label: 'For Administrators', href: '/administrators' },
        { label: 'For Districts', href: '/districts' },
        { label: 'For Tutoring Centers', href: '/tutoring' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Blog', href: '/blog' },
        { label: 'Case Studies', href: '/case-studies' },
        { label: 'Research', href: '/research' },
        { label: 'Webinars', href: '/webinars' },
        { label: 'Help Center', href: '/help' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Careers', href: '/careers' },
        { label: 'Press', href: '/press' },
        { label: 'Partners', href: '/partners' },
        { label: 'Contact', href: '/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy-policy' },
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Cookie Policy', href: '/cookies' },
        { label: 'FERPA Compliance', href: '/ferpa' },
        { label: 'Data Security', href: '/security' },
      ],
    },
  ];

  const socialLinks = [
    { icon: <LogoTwitter size={24} />, href: 'https://twitter.com/sigma08', label: 'Twitter' },
    { icon: <LogoLinkedin size={24} />, href: 'https://linkedin.com/company/sigma08', label: 'LinkedIn' },
    { icon: <LogoFacebook size={24} />, href: 'https://facebook.com/sigma08', label: 'Facebook' },
    { icon: <LogoYoutube size={24} />, href: 'https://youtube.com/sigma08', label: 'YouTube' },
  ];

  return (
    <footer className="marketing-footer">
      <Grid className="footer-main">
        <Column lg={16} md={8} sm={4}>
          <div className="footer-content">
            {/* Company Info Section */}
            <div className="footer-company">
              <h2 className="footer-logo">Sigma08</h2>
              <p className="footer-tagline">Transforming education through AI-powered personalized learning</p>

              <div className="footer-contact">
                <div className="contact-item">
                  <Email size={16} />
                  <span>support@sigma08.com</span>
                </div>
                <div className="contact-item">
                  <Phone size={16} />
                  <span>1-800-SIGMA08</span>
                </div>
                <div className="contact-item">
                  <Location size={16} />
                  <span>San Francisco, CA</span>
                </div>
              </div>

              <div className="footer-social">
                {socialLinks.map((social, index) => (
                  <Link
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="social-link"
                  >
                    {social.icon}
                  </Link>
                ))}
              </div>
            </div>

            {/* Footer Links Sections */}
            <div className="footer-links">
              {footerSections.map((section, index) => (
                <div key={index} className="footer-section">
                  <h3 className="section-title">{section.title}</h3>
                  <ul className="section-links">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link href={link.href} className="footer-link">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Column>
      </Grid>

      {/* Compliance Section */}
      <div className="footer-compliance">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div className="compliance-content">
              <div className="compliance-badges">
                <Tag type="green" size="md">
                  <CheckmarkFilled /> FERPA Compliant
                </Tag>
                <Tag type="blue" size="md">
                  <Security /> SOC 2 Type II
                </Tag>
                <Tag type="purple" size="md">
                  COPPA Certified
                </Tag>
                <Tag type="cyan" size="md">
                  GDPR Ready
                </Tag>
                <Tag type="magenta" size="md">
                  ISO 27001
                </Tag>
              </div>

              <div className="footer-bottom">
                <p className="copyright">© {currentYear} Sigma08 Education Technology. All rights reserved.</p>
                <div className="footer-bottom-links">
                  <Link href="/privacy-policy" className="bottom-link">
                    Privacy
                  </Link>
                  <span className="separator">•</span>
                  <Link href="/terms" className="bottom-link">
                    Terms
                  </Link>
                  <span className="separator">•</span>
                  <Link href="/cookies" className="bottom-link">
                    Cookies
                  </Link>
                  <span className="separator">•</span>
                  <Link href="/accessibility" className="bottom-link">
                    Accessibility
                  </Link>
                  <span className="separator">•</span>
                  <Link href="/sitemap" className="bottom-link">
                    Sitemap
                  </Link>
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>
    </footer>
  );
};

export default MarketingFooter;
